import { formatMoney, formatNumber } from '@/i18n/format';
import { formatPriceCents } from '@/constants/billing-display';
import type { billingStrings } from '@/i18n/dictionaries/billing';
import type { CheckoutPeriod, PlanTerms } from '@/types/billing';

/**
 * WEB-3302 юнит «условия» — чистые функции «условия плана → строки» для окна
 * «Тарифы и условия» (`PlanTermsModal.tsx`, эталон
 * `design/cabinet-rescin/sections/org.jsx:905-930`). Окно только рисует.
 *
 * Деньги (R4): кабинет ничего не считает, только печатает числа ответа
 * `GET /billing` (`planTerms`, WEB-4008). Единицы — как в контракте:
 *  - суммы тарифа и порога — центы, `formatPriceCents`: знак «$» впереди,
 *    разряды по языку кабинета (ru «$2 508», en «$2,508»; WEB-4180, решение
 *    владельца 03.11); границы поступлений — компактно закреплённым `en`
 *    («$50K»), разрядов в них нет;
 *  - ставка за переход — `rateMicros` = миллионные доли доллара (÷ 1 000 000),
 *    от трёх до шести знаков после точки, закреплённым `en`;
 *  - процент с поступлений — `rateBps` = сотые доли процента (÷ 100), языком
 *    кабинета;
 *  - границы ступеней переходов — штуки, компактно языком кабинета
 *    («50 тыс.», «50K»).
 * Выгода года — `annualSavingsCents` ответа, не «12 × месяц − год».
 */

type Strings = (typeof billingStrings)['en'];

/** Подпись ступени и её значение (строка `tl` эталона). */
export interface TierLine {
  readonly label: string;
  readonly value: string;
}

/** Ставка за переход: миллионные доли валюты → «$0.030». */
export function exitRate(rateMicros: number, currency: string): string {
  return formatMoney(
    rateMicros / 1_000_000,
    currency,
    { minimumFractionDigits: 3, maximumFractionDigits: 6 },
    'en',
  );
}

/** Процент с поступлений: сотые доли процента → «1,75%». */
export function revenuePercent(rateBps: number): string {
  return `${formatNumber(rateBps / 100, { maximumFractionDigits: 2 })}%`;
}

/** Счёт переходов компактно: «50 тыс.» / «50K». */
function exits(n: number): string {
  return formatNumber(n, { notation: 'compact' });
}

/** Граница поступлений компактно, закреплённым en: «$50K». */
function revenueBound(cents: number, currency: string): string {
  return formatMoney(cents / 100, currency, { notation: 'compact' }, 'en');
}

/**
 * Подписи ступеней по границам: первая с границей — «до N», средние —
 * «A–B», последняя без границы — «свыше A», единственная без границы — `any`.
 */
function bandLabels<T>(
  tiers: readonly T[],
  upTo: (t: T) => number | null,
  fmt: (n: number) => string,
  words: {
    upTo: (n: string) => string;
    range: (a: string, b: string) => string;
    above: (n: string) => string;
    any: string;
  },
): string[] {
  return tiers.map((t, i) => {
    const prev = i > 0 ? upTo(tiers[i - 1]) : null;
    const to = upTo(t);
    if (to === null) return prev === null ? words.any : words.above(fmt(prev));
    return prev === null ? words.upTo(fmt(to)) : words.range(fmt(prev), fmt(to));
  });
}

/** Ступени платы за переходы: бесплатные в месяц (если есть) и `exitTiers`. */
export function exitTierLines(terms: PlanTerms, s: Strings): TierLine[] {
  const { freeExitsPerMonth, exitTiers } = terms.usage;
  const labels = bandLabels(exitTiers, (t) => t.upTo, exits, {
    upTo: s.tierUpTo,
    range: s.tierRange,
    above: s.tierAbove,
    any: s.tierAny,
  });
  const lines = exitTiers.map((t, i) => ({
    label: labels[i],
    value: exitRate(t.rateMicros, terms.currency),
  }));
  return freeExitsPerMonth > 0
    ? [{ label: s.tierFirst(formatNumber(freeExitsPerMonth)), value: s.tierFree }, ...lines]
    : lines;
}

/** Ступени процента с поступлений (`revenueTiers`). */
export function revenueTierLines(terms: PlanTerms, s: Strings): TierLine[] {
  const { revenueTiers } = terms.usage;
  const labels = bandLabels(
    revenueTiers,
    (t) => t.upToCents,
    (n) => revenueBound(n, terms.currency),
    { upTo: s.pctUpTo, range: s.tierRange, above: s.pctAbove, any: s.pctAny },
  );
  return revenueTiers.map((t, i) => ({
    label: labels[i],
    value: revenuePercent(t.rateBps),
  }));
}

/**
 * Шапка платной колонки: «в месяц» по периоду (`monthlyChargeCents` либо
 * `annualMonthlyCents`) и у года — списание за год и выгода из ответа.
 */
export function planHead(
  terms: PlanTerms,
  period: CheckoutPeriod,
  s: Strings,
): { price: string; yearNote: string | null } {
  const t = terms.tariff;
  const money = (cents: number) => formatPriceCents(cents, terms.currency);
  if (period === 'month') return { price: money(t.monthlyChargeCents), yearNote: null };
  return {
    price: money(t.annualMonthlyCents),
    yearNote:
      t.annualSavingsCents > 0
        ? s.prYearSave(money(t.annualChargeCents), money(t.annualSavingsCents))
        : s.prYearOnly(money(t.annualChargeCents)),
  };
}

/** «Пробный: N дней или M переходов, нужна карта» — `planTerms.trial`. */
export function trialLine(terms: PlanTerms, s: Strings): string {
  const { days, exitLimit } = terms.trial;
  return s.prTrial(days, exitLimit, formatNumber(exitLimit));
}

/** «$N накопилось — сразу с карты, …» — `chargeThresholdCents`. */
export function thresholdLine(terms: PlanTerms, s: Strings): string {
  return s.prThreshold(
    formatPriceCents(terms.usage.chargeThresholdCents, terms.currency),
  );
}
